import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import clsx from "clsx";
import { Button } from "@heroui/react";
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from "@heroicons/react/24/outline";

import InfiniteCanvas from "./InfiniteCanvas";
import BoardViewer from "./BoardViewer";
import { themes } from "./RenderHelpers";
import MainStore, { uniquePrimitives } from "./MainStore";
import { buildCanvasClickHandlers } from "./utils/canvasClickHandlers";
import AgentChat from "./AgentChat";
import useDataEvent from "./CustomHook";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "./@components/description-list";
import { PrimitiveCard } from "./PrimitiveCard";
import FilterHierarchy from "./FilterHierarchy";

export default function PageView({ primitive }) {
    const mainstore = MainStore();
    const canvasRef = useRef();
    const boardStateRef = useRef({});
    const [refreshKey, triggerRefresh] = useReducer((x) => x + 1, 0);
    const [chatDocked, setChatDocked] = useState(true);
    const [agentStatus, setAgentStatus] = useState({});

    const watchIds = useMemo(() => {
        if (!primitive) {
            return [];
        }
        const descendantIds = primitive.primitives?.uniqueAllIds ?? [];
        return [primitive.id, descendantIds].flat();
    }, [primitive?.id, primitive?.primitives?.uniqueAllIds]);

    useDataEvent(
        "relationship_update set_field set_parameter set_title new_child delete_primitive",
        watchIds,
        () => {
            triggerRefresh();
            return false;
        }
    );

    useEffect(() => {
        boardStateRef.current = {};
    }, [primitive?.id]);


    function buildBoard(){
        if (!primitive) {
            return [];
        }

        const state = boardStateRef.current;
        state.renderSubPages = true;
        state.showSlideSuggestions = true
        state.hideWidgets = true;
        state[primitive.id] = state[primitive.id] ?? { id: primitive.id, renderSubPages: true };

        const themeKey = primitive.renderConfig?.theme ?? primitive.configParent?.renderConfig?.theme ?? "default";
        const theme = themes[themeKey] || themes.default;

        BoardViewer.prepareBoard(primitive, state);
        const view = BoardViewer.renderBoardView(primitive, primitive, state, { theme });
        return view ? [view] : [];
    }

    const renderedSet = useMemo(() => {
        return buildBoard()
    }, [primitive?.id]);

    useEffect(()=>{
        const update = buildBoard()[0]
        if( update && canvasRef.current){
            canvasRef.current.refreshFrame( primitive.id, update)
        }
    },[primitive?.renderConfig?.theme, refreshKey])

    const pinEntries = useMemo(() => {
        if (!primitive) {
            return [];
        }
        const pins = primitive.inputPins ?? {};
        return Object.entries(pins);
    }, [primitive?.id, refreshKey]);

    const pinStatus = primitive?.inputPinsWithStatus ?? {};
    const selectItems = (...args) => {
        mainstore.sidebarSelect(...args);
    };

    const resolveRenderContext = useCallback((renderContextId) => {
        if (!renderContextId) {
            return undefined;
        }
        const pageId = primitive?.id;
        if (!pageId) {
            return undefined;
        }
        const map = boardStateRef.current?.[pageId]?.subpages?.[pageId]?.renderContexts;
        return map?.get ? map.get(renderContextId) : undefined;
    }, [primitive?.id]);

    const callbacks = useMemo(() => {
        return buildCanvasClickHandlers({
            mainstore,
            selectItems,
            uniquePrimitives,
            resolveRenderContext,
            onPrimitiveClickPre: (id) => {
                const state = boardStateRef.current;
                const viewState = state?.[id];
                if (viewState?.variant && viewState?.primitive?.type === "page") {
                    viewState.variant = null;
                    BoardViewer.prepareBoard(viewState.primitive, state);
                    canvasRef.current?.refreshFrame(viewState.primitive.id);
                    return true;
                }
                return false;
            }
        });
    }, [mainstore, resolveRenderContext]);

    const agentScope = useMemo(() => {
        if (!primitive) {
            return undefined;
        }
        return { constrainTo: primitive.origin?.id };
    }, [primitive?.id]);


    const pageHasRender = renderedSet.length > 0;

    return (
        <div className="flex h-full min-h-0 w-full space-x-4 bg-gray-50 p-4">
            {chatDocked && (
                <div className="flex h-full w-1/4 min-w-[24rem] shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                        <p className="text-sm font-semibold text-slate-600">Sense AI</p>
                        <Button
                            isIconOnly
                            size="sm"
                            variant="light"
                            aria-label="Undock chat"
                            onClick={() => setChatDocked(false)}
                        >
                            <ArrowsPointingOutIcon className="h-5 w-5 text-slate-500" />
                        </Button>
                    </div>
                    {false && <div className="flex h-full flex-col overflow-hidden p-3 text-sm"></div>}

                    <div className={clsx([
                                        'flex h-full flex-col overflow-hidden p-3 text-sm w-full',
                                        agentStatus.hasReplies ? "h-full " : "pt-[30vh]",
                                    ])}>

                        {!agentStatus.hasReplies && (
                            <div className="mb-8 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-slate-600 w-[60%] mx-auto">
                                Ask Sense AI for help with configuring this page or interpreting its data.
                            </div>
                        )}
                        <div className="flex min-h-0 flex-col overflow-hidden h-full">
                            <AgentChat
                                primitive={primitive}
                                context={primitive}
                                showContext={false}
                                scope={agentScope}
                                setStatus={setAgentStatus}
                                seperateInput={true}
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden">
                    <div className="flex min-h-0 flex-1 flex-row space-x-4 overflow-hidden">
                        <div className="flex h-full w-[28rem] shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-200 px-4 py-3">
                                <p className="text-sm font-semibold text-slate-600">Data inputs</p>
                                <p className="text-xs text-slate-500">Provide values to personalize this page.</p>
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-3">
                                <FilterHierarchy root={primitive.importsHierarchyAnnotated} page={primitive} />
                            </div>
                        </div>
                        <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                            {pageHasRender ? (
                                <InfiniteCanvas
                                    ref={canvasRef}
                                    primitive={primitive}
                                    initialZoom="width"
                                    board
                                    hideWidgets
                                    background="#fdfdfd"
                                    ignoreAfterDrag={false}
                                    showPins={false}
                                    enableShapeSelection={false}
                                    enableFrameSelection
                                    events={{
                                        wheel: {
                                            passive: false
                                        }
                                    }}
                                    callbacks={callbacks}
                                    highlights={{
                                        primitive: "border",
                                        cell: "background",
                                        widget: "background"
                                    }}
                                    render={renderedSet}
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-sm text-slate-500">
                                    This page has no content to display yet.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
